import {
    Component,
    ElementRef,
    forwardRef,
    Host,
    Input,
    OnInit,
    Optional,
    SkipSelf,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';

import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {MatDatepicker, MatDatepickerInputEvent} from '@angular/material/datepicker';
import {MomentDateAdapter} from '@angular/material-moment-adapter';
import {ControlContainer, NG_VALUE_ACCESSOR} from '@angular/forms';

import * as moment from 'moment';

import {BaseComponent, DatepickerControl} from '../../classes';
import {environment} from '../../../../environments/environment';

@Component({
    selector: 'app-datepicker',
    templateUrl: './datepicker.component.html',
    styleUrls: ['./datepicker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatepickerComponent),
            multi: true
        },
        {provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE]},

        {
            provide: MAT_DATE_FORMATS, useValue: {
                parse: {
                    dateInput: environment.dateFormat,
                },
                display: {
                    dateInput: environment.dateFormat,
                    monthYearLabel: 'MMM YYYY',
                    dateA11yLabel: environment.dateFormat,
                    monthYearA11yLabel: 'MMMM YYYY',
                },
            }
        },
    ],
    encapsulation: ViewEncapsulation.None
})
export class DatepickerComponent
    extends BaseComponent<string, MatDatepickerInputEvent<moment.Moment>, DatepickerControl> implements OnInit {
    @ViewChild('datepickerInput', {static: true}) public datepickerInputRef: ElementRef;
    @ViewChild('datepicker', {static: true}) public datepickerRef: MatDatepicker<moment.Moment>;

    constructor(
        @Optional() @Host() @SkipSelf()
        protected controlContainer: ControlContainer
    ) {
        super(controlContainer);
    }

    protected setDate(value: moment.Moment): string {
        if (!value) {
            return '';
        }

        return value.utc(true)
            .toISOString();
    }

    ngOnInit() {
        this.asFormsControl();
        this.checkInvalid();
    }

    openDatePickerWithTimeout() {
        const datepickerRef = this.datepickerRef;
        setTimeout(
            () => {
                datepickerRef.open();
            },
            50
        );
    }

    openDatepicker(datepicker: MatDatepicker<moment.Moment>): void {
        if (this.asFormsControl().startDateAt) {
            this.openDatePickerWithTimeout();

            return;
        }
        datepicker.opened = !datepicker.opened;
    }

    resetDatepicker(): void {
        this.datepickerRef.startAt = null;
        this.datepickerRef.select(null);

        this.datepickerInputRef.nativeElement.value = null;

        this.control.value = null;
        this.control.resetErrors();

        this.checkInvalid();

        super.writeValue(null);
    }

    changeValue(event: MatDatepickerInputEvent<moment.Moment>): void {
        const date = this.setDate(event.value);

        this.onChange(date);
        this.checkInvalid();
        this.control.resetErrors();

        super.writeValue(date);
    }

    writeValue(value: string): void {
        this.checkInvalid();

        this.control.resetErrors();

        const date = moment(value);

        if (date.isValid()) {
            this.datepickerRef.startAt = date;

            this.datepickerInputRef.nativeElement.value = date.format(environment.dateFormat);

            const isoDate = this.setDate(date);

            super.writeValue(isoDate);
        } else {
            this.resetDatepicker();
        }
    }

    asFormsControl(): DatepickerControl {
        if (!this.control) {
            this.control = new DatepickerControl({
                name: '',
                value: '',
                placeholder: '',
                validators: [],
                componentType: 0,
                invalid: false,
                errors: [],
                disabled: false,
                minDate: null,
                maxDate: null,
                startDateAt: null,
                more: [],
                resetable: false
            });
        }

        return (this.control as DatepickerControl);
    }
}
